import { useEffect, useId, useMemo, useState } from 'react';
import { DialogContent } from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { useFetchDocumentsQuery } from '@api/Trinium/Endpoints/Document';
import { useLazyFetchDocumentsQuery } from '@api/Trinium/Endpoints/Document/slice';
import {
  useSendPudlReceiptMutation,
  useSendPudlReceiptsBatchMutation,
  useWarmupPudlReceiptMutation
} from '@api/Trinium/Endpoints/PickupDeliveryReceipt/slice';
import type { SendDataResponse, SendPudlReceiptsBatchResponse } from '@api/Trinium/Endpoints/PickupDeliveryReceipt/types';
import { getApiErrorMessage } from '@api/getApiError';
import { confirmAsync } from '@components/Modals/ConfirmPopup';
import FormikModal from '@components/Modals/FormikModal';
import FormikModalHeader from '@components/Modals/FormikModalHeader';
import LegOrderHeader from '@components/Modals/Headers/LegOrderHeader';
import { useModalProvider } from '@components/Modals/ModalContext';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { isFormikErrorsEmpty } from '@hooks/useFormikValues';
import { useAppDispatch } from '@reduxStore';
import { clearToast, queueControlledToast, queueUncontrolledToast } from '@toast';
import Form from '../PickupDeliveryReceipt/Form';
import {
  SendPudlReceiptForm,
  SendPudlReceiptProps,
  formatOrderNo,
  getSendPudlReceiptValidationSchema,
  initialFormValues,
  splitEmailList
} from './types';

import styles from './SendPudleReceipt.module.css';

/** Turns the batch send result into a single toast summarizing what was and was not sent. */
function summarizeSendResult(result: SendPudlReceiptsBatchResponse, legs: DispatchLeg[], values: SendPudlReceiptForm, drvEmailOverride?: string) {
  const legsById = new Map(legs.map((leg) => [leg.legId, leg]));
  const sentCount = result.receipts.filter((receipt) => receipt.emailed || receipt.texted).length;
  const problems = result.receipts
    .filter((receipt) => receipt.errors)
    .map((receipt) => {
      const leg = legsById.get(receipt.legId);
      return `${leg ? formatOrderNo(leg) : String(receipt.legId)}: ${receipt.errors ?? ''}`;
    });

  const sentTo = [values.sendEmail && 'emailed', values.sendText && 'texted'].filter(Boolean).join(' and ');
  const parts = [`Pickup/Delivery Receipts ${sentTo} to the assigned driver on ${String(sentCount)} of ${String(legs.length)} legs.`];
  if (drvEmailOverride) parts.push(`Driver emails were sent only to ${drvEmailOverride}.`);
  if (result.copyEmailed) parts.push(`A copy of all receipts was emailed to ${values.copyEmailAddr ?? ''}.`);
  if (result.copyTexted) parts.push(`A copy of all receipts was texted to ${values.cellPhone}.`);
  if (problems.length > 0) parts.push(`Not sent to driver - ${problems.join(' | ')}`);
  if (result.copyErrors) parts.push(result.copyErrors);

  const anythingSent = sentCount > 0 || result.copyEmailed || result.copyTexted;
  const hasProblems = problems.length > 0 || Boolean(result.copyErrors);
  const severity = !anythingSent ? 'error' : hasProblems ? 'warning' : 'success';

  return { message: parts.join(' '), severity } as const;
}

/**
 * Send HTML Pickup/Delivery Receipt form component.  Given multiple legs, every leg's receipt is sent to its own driver.
 */
export default function SendPudlReceipt(props: SendPudlReceiptProps) {
  const { number: terminalNumber, incCustInfo } = useCurrentTerminal();
  const { queueModalAnnouncement } = useModalProvider();
  const appDispatch = useAppDispatch();
  const formId = useId();
  const [isOpen, setIsOpen] = useState(true);
  const batchLegs = props.legs && props.legs.length > 1 ? props.legs : undefined;
  /** Legs of different drivers can't be emailed or texted to one driver, so their receipts are only downloaded. */
  const printOnly = Boolean(batchLegs && props.printOnly);
  const validationSchema = useMemo(
    () => (printOnly ? undefined : getSendPudlReceiptValidationSchema(Boolean(batchLegs))),
    [batchLegs, printOnly]
  );

  const [putPudlReceipt] = useSendPudlReceiptMutation();
  const [sendReceiptsBatch] = useSendPudlReceiptsBatchMutation();
  const [warmupPudlReceipt] = useWarmupPudlReceiptMutation();
  const [fetchDocuments] = useLazyFetchDocumentsQuery();
  const [sendDataResponse, setSendDataResponse] = useState<SendDataResponse | undefined>(undefined);
  /** Every selected leg's generated text message, shown together so they can be reviewed (and replaced) before sending. */
  const [batchTextPreview, setBatchTextPreview] = useState<string>();

  useEffect(() => {
    if (printOnly) return;

    if (batchLegs) {
      void Promise.all(
        batchLegs.map((leg) =>
          warmupPudlReceipt({ terminalNumber, legId: leg.legId, includeAllLegs: false, includeCustInfo: incCustInfo })
            .unwrap()
            .then((receiptData) => `Order# ${formatOrderNo(leg)}\r${receiptData.textBody}`)
        )
      )
        .then((messages) => {
          setBatchTextPreview(messages.join('\r\r--------------------\r\r'));
        })
        .catch(() => {
          queueModalAnnouncement({
            message: 'Failed to retrieve Pickup/Delivery text preview data.',
            severity: 'error'
          });
        });
      return;
    }

    const loadWarmupData = async () => {
      try {
        const receiptData = await warmupPudlReceipt({
          terminalNumber: terminalNumber,
          legId: props.legId,
          includeAllLegs: false,
          includeCustInfo: incCustInfo
        }).unwrap();

        setSendDataResponse(receiptData);
      } catch {
        queueModalAnnouncement({
          message: 'Failed to retrieve Pickup/Delivery text preview data.',
          severity: 'error'
        });
      }
    };

    void loadWarmupData();
  }, [batchLegs, incCustInfo, printOnly, props.legId, queueModalAnnouncement, terminalNumber, warmupPudlReceipt]);

  const { data: documents } = useFetchDocumentsQuery(
    {
      terminalNumber: terminalNumber,
      consignmentId: props.csnId
    },
    {
      skip: Boolean(batchLegs),
      refetchOnMountOrArgChange: true
    }
  );

  const hasHazmatPaperwork = useMemo(() => {
    if (!documents) {
      return false;
    }

    return documents.find((document) => {
      return document.typeCode === 'HZ' && document.isScanned;
    });
  }, [documents]);

  /** Initial Formik Values */
  const initialValues = useMemo(() => {
    if (batchLegs) {
      const lines = batchLegs.map((leg) => `Order# ${formatOrderNo(leg)} | Container No.: ${leg.cntrId ?? ''} | Customer Reference: ${leg.csnCustRefNo ?? ''}`);
      return {
        ...initialFormValues,
        incCustInfo: incCustInfo,
        copyEmailAddr: '',
        // Every selected order belongs to the same driver, so their address is shown
        emailAddr: props.driverEmail ?? '',
        emailSubject: `Pickup/Delivery Receipts | ${String(batchLegs.length)} Legs`,
        emailBody: `${lines.join('\r')}\r\rPlease see file attached`,
        textBody: batchTextPreview ?? ''
      };
    }

    const subjectRefNo = props.isExport ? `Bkg# ${props.bookingRefNo || 'Unknown'}` : `Cntr# ${props.cntrId || 'Unknown'}`;

    return {
      ...initialFormValues,
      incCustInfo: incCustInfo,
      emailSubject: `Order# ${props.jobId.toString().padStart(9, '0')} | ${subjectRefNo} | Pickup/Delivery Receipt`,
      emailBody: `Container No.: ${props.cntrId ?? ''}\rCustomer Reference: ${props.custRefNo ?? ''}\r\rPlease see file attached`,
      cellPhone: props.driverCellPhone?.replaceAll(/\D/g, '') ?? '',
      emailAddr: props.driverEmail ?? '',
      textBody: sendDataResponse?.textBody ?? ''
    };
  }, [batchLegs, batchTextPreview, incCustInfo, props, sendDataResponse]);

  /** Same check as a single send: hazmat import orders should have scanned hazmat paperwork before receipts go out. */
  const confirmMissingHazmatPaperwork = async (legs: DispatchLeg[]) => {
    const hazmatOrders = new Map(
      legs.filter((leg) => leg.reqHazmatCert && leg.jobCategoryTransportationType === 'Import').map((leg) => [leg.csnId, formatOrderNo(leg)])
    );
    const missingOrders = (
      await Promise.all(
        Array.from(hazmatOrders).map(async ([csnId, orderNo]) => {
          try {
            const orderDocuments = await fetchDocuments({ terminalNumber, consignmentId: csnId }).unwrap();
            return orderDocuments.some((document) => document.typeCode === 'HZ' && document.isScanned) ? undefined : orderNo;
          } catch {
            return orderNo;
          }
        })
      )
    ).filter((orderNo) => orderNo !== undefined);

    if (missingOrders.length === 0) return true;
    return confirmAsync(`No Hazmat Paperwork exists for ${missingOrders.join(', ')}, do you still want to send the Pickup/Delivery Receipts?`);
  };

  // The backend trims the addresses, strips the phone number to digits, and ignores the copy fields of a delivery option that is off.
  async function sendBatch(values: SendPudlReceiptForm, legs: DispatchLeg[]) {
    if (!(await confirmMissingHazmatPaperwork(legs))) return;
    setIsOpen(false);

    // A changed Email Address replaces the driver as the recipient; left as the driver's (or blank), the driver and terminal default receive it.
    const drvEmailOverride =
      values.emailAddr !== initialValues.emailAddr ? splitEmailList(values.emailAddr).join(',') || undefined : undefined;

    // The modal closes on submit, so a toast is the only signal the request is still running.
    const toastId = Math.floor(Math.random() * 10000);
    appDispatch(
      queueControlledToast({
        id: toastId,
        type: 'message',
        message: `Sending ${String(legs.length)} Pickup/Delivery Receipts. This may take a moment...`
      })
    );

    void sendReceiptsBatch({
      terminalNumber,
      legs,
      data: {
        sendEmail: values.sendEmail,
        emailAddr: splitEmailList(values.copyEmailAddr ?? '').join(','),
        emailSubject: values.emailSubject,
        emailBody: values.emailBody,
        sendText: values.sendText,
        cellPhone: values.cellPhone,
        // Left as previewed, each driver gets their own leg's message; edited, the text goes to every driver.
        textBody: values.textBody === batchTextPreview ? '' : values.textBody,
        incAllLegs: values.incAllLegs,
        incCustInfo: values.incCustInfo,
        drvEmailOverride,
        // Likewise left as defaulted, each driver's email gets its own leg's subject and body; edited, they go to every driver.
        ...(values.emailSubject !== initialValues.emailSubject && { drvEmailSubject: values.emailSubject }),
        ...(values.emailBody !== initialValues.emailBody && { drvEmailBody: values.emailBody })
      }
    })
      .unwrap()
      .then((result) => {
        appDispatch(queueUncontrolledToast({ type: 'message', ...summarizeSendResult(result, legs, values, drvEmailOverride) }));
      })
      .catch((error: unknown) => {
        appDispatch(queueUncontrolledToast({ type: 'message', message: getApiErrorMessage(error), severity: 'error' }));
      })
      .finally(() => {
        appDispatch(clearToast(toastId));
      });
  }

  async function onSubmitForm(values: SendPudlReceiptForm, formikHelpers: FormikHelpers<SendPudlReceiptForm>) {
    if (printOnly) return;

    if (batchLegs) {
      await sendBatch(values, batchLegs);
      return;
    }

    if (props.isHazmat && props.isImport && !hasHazmatPaperwork) {
      const confirmed = await confirmAsync('No Hazmat Paperwork exists, do you still want to send the Pickup/Delivery Receipt?');
      if (!confirmed) return;
    }

    const validationErrors = await formikHelpers.validateForm(values);
    if (isFormikErrorsEmpty(validationErrors)) {
      void putPudlReceipt({
        terminalNumber: terminalNumber,
        legId: props.legId,
        csnId: props.csnId,
        jobId: String(props.jobId),
        type: {
          action: 'sendReceipt',
          data: {
            ...values
          }
        }
      });
      setIsOpen(false);
    }
  }

  return (
    <FormikModal
      isOpen={isOpen}
      onClose={() => {
        setIsOpen(false);
      }}
      paperClass={styles['pudl-receipt-modal']}>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        validateOnMount
        onSubmit={onSubmitForm}>
        {(formikProps) => (
          <>
            <FormikModalHeader
              title={batchLegs ? `Pickup/Delivery Receipts (${String(batchLegs.length)} Legs)` : 'Pickup/Delivery Receipt'}
              formName={formId}
              onClose={() => {
                setIsOpen(false);
              }}
              submitButtonText='Send'
              submitButtonMessage='Send & Close'
              hideSubmit={printOnly}
              areYouSurePrompt={`${batchLegs ? 'Pickup/Delivery Receipts were' : 'Pickup/Delivery Receipt was'} not sent, are you sure you wish to leave the form?`}
            />

            {!batchLegs && (
              <LegOrderHeader
                terminalNumber={terminalNumber}
                legId={props.legId}
              />
            )}

            <DialogContent>
              <Form
                id={formId}
                legId={props.legId}
                batchLegs={batchLegs}
                printOnly={printOnly}
                {...formikProps}
              />
            </DialogContent>
          </>
        )}
      </Formik>
    </FormikModal>
  );
}
